import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Bell,
  Camera,
  CheckCircle2,
  Clock,
  Droplets,
  FileText,
  MapPin,
  QrCode,
  RefreshCw,
  Send,
  Settings,
  ShieldAlert,
  Signal,
  SignalZero,
  Thermometer,
  UploadCloud,
  Waves
} from 'lucide-react';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase/client';
import { aqlVisionApiUrl, functionsBaseUrl, publicAnonKey } from '/utils/supabase/info';
import { AqlVisionMark } from '@/app/components/branding/aql-vision-brand';
import { AqlVisionLoader } from '@/app/components/branding/aql-vision-loader';
import { UniversalSmartLogin } from '@/app/components/universal-smart-login';
import { useGlobal } from '@/app/providers/global-provider';
import { fetchProjects, fetchZones, type AqlOption } from '@/app/services/growth-service';
import {
  DEMO_AUTH_TOKEN,
  DEMO_POSTER_FILE,
  DEMO_USER_EMAIL,
  getDemoAssetUrl,
  getDemoDashboardSummary,
  getDemoVisionAlerts
} from '@/app/utils/vision-demo-data';
import {
  normalizeAlertsToCases,
  type ClinicalCase,
  type RawVisionAlert,
  type Severity
} from '@/app/utils/vision-biology';
import type { MvpDashboardSummary } from '@/app/components/vision-dashboard';

type MobileTab = 'status' | 'alerts' | 'observe' | 'asset' | 'settings';
type SyncEventType = 'validate_alert' | 'field_observation';

type SyncEvent = {
  id: string;
  type: SyncEventType;
  createdAt: string;
  attempts: number;
  payload: Record<string, any>;
  lastError?: string;
};

type ObservationForm = {
  zoneId: string;
  category: string;
  severity: Severity;
  note: string;
};

const KV_STORE_TABLE = 'kv_store_113fe7c2';
const SESSION_TOKEN_KEYS = ['aql_auth_token', 'visionAlertsToken', 'sessionToken'];
const SESSION_EMAIL_KEYS = ['aql_user_email', 'visionAlertsEmail', 'userEmail'];
const MOBILE_SYNC_QUEUE_KEY = 'aql_mobile_sync_queue_v1';
const MOBILE_OBSERVATION_DRAFT_KEY = 'aql_mobile_observation_draft_v2';
const LOGIN_CLIENT_CONFIRMED_KEY = 'aql_login_client_confirmed_vision-alerts';

const severityOrder: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
};

const readStoredValue = (keys: string[]) => {
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }
  return null;
};

const parseJson = (value: unknown) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const stringFrom = (value: unknown) => {
  const next = String(value ?? '').trim();
  return next || null;
};

const dateFrom = (value: unknown) => {
  const date = new Date(String(value ?? ''));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortTime = (value: unknown) => {
  const date = dateFrom(value);
  if (!date) return 'sem dados';
  return date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

const formatRelative = (value: unknown) => {
  const date = dateFrom(value);
  if (!date) return 'sem dados';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
};

const nowIso = () => new Date().toISOString();

const newEventId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readSyncQueue = (): SyncEvent[] => {
  try {
    const raw = localStorage.getItem(MOBILE_SYNC_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSyncQueue = (queue: SyncEvent[]) => {
  localStorage.setItem(MOBILE_SYNC_QUEUE_KEY, JSON.stringify(queue));
};

const readObservationDraft = (): ObservationForm => {
  try {
    const raw = localStorage.getItem(MOBILE_OBSERVATION_DRAFT_KEY);
    if (raw) {
      const value = JSON.parse(raw);
      return {
        zoneId: String(value.zoneId ?? ''),
        category: String(value.category ?? 'observacao_campo'),
        severity: (value.severity as Severity) ?? 'medium',
        note: String(value.note ?? '')
      };
    }
  } catch {
    // Fall through to the default draft.
  }

  return { zoneId: '', category: 'observacao_campo', severity: 'medium', note: '' };
};

const sanitizeInput = (value: string, maxLength = 600) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const parseResponsePayload = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const normalizeRowToAlert = (row: Record<string, any>, fallbackPrefix: string): RawVisionAlert => {
  const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const createdAt = stringFrom(row.detected_at ?? row.detectedAt ?? row.created_at ?? row.createdAt ?? row.updated_at)
    ?? new Date().toISOString();
  const id = stringFrom(row.case_id ?? row.alert_id ?? row.id ?? row.key) ?? `${fallbackPrefix}-${createdAt}`;

  return {
    ...row,
    id,
    case_id: stringFrom(row.case_id) ?? undefined,
    alert_id: stringFrom(row.alert_id) ?? undefined,
    client_id: stringFrom(row.client_id ?? metadata.client_id) ?? undefined,
    imageUrl: stringFrom(row.image_url ?? row.imageUrl ?? metadata.image_url ?? metadata.imageUrl) ?? undefined,
    image_url: stringFrom(row.image_url ?? row.imageUrl ?? metadata.image_url ?? metadata.imageUrl) ?? undefined,
    species: stringFrom(row.species ?? metadata.species) ?? undefined,
    biological_phase: stringFrom(row.biological_phase ?? row.phase ?? metadata.biological_phase ?? metadata.phase) ?? undefined,
    detected_at: stringFrom(row.detected_at ?? row.detectedAt ?? row.created_at ?? row.createdAt) ?? undefined,
    model_confidence: row.model_confidence ?? row.confidence ?? metadata.model_confidence ?? metadata.confidence,
    alert_type: stringFrom(row.alert_type ?? row.alertType ?? row.type ?? metadata.alert_type ?? metadata.alertType) ?? undefined,
    severity: (stringFrom(row.severity ?? metadata.severity) as Severity | null) ?? undefined,
    status: row.status ?? metadata.status,
    problemName: stringFrom(row.problem_name ?? row.problemName ?? metadata.problem_name ?? metadata.problemName) ?? undefined,
    problemDescription: stringFrom(row.problem_description ?? row.problemDescription ?? metadata.problem_description ?? metadata.problemDescription) ?? undefined,
    metadata,
    createdAt,
    created_at: stringFrom(row.created_at) ?? undefined,
    updatedAt: stringFrom(row.updated_at ?? row.updatedAt) ?? undefined,
    validation_result: row.validation_result ?? row.validationResult,
    validation_notes: stringFrom(row.validation_notes ?? row.validationNotes ?? row.notes) ?? undefined
  };
};

const normalizeKvAlert = (row: Record<string, any>): RawVisionAlert | null => {
  const value = parseJson(row.value);
  if (!value || typeof value !== 'object') return null;
  return normalizeRowToAlert({ key: row.key, ...(value as Record<string, any>) }, 'kv-alert');
};

const applyScope = (query: any, clientId?: string | null, projectId?: string | null) => {
  let next = query;
  if (clientId) next = next.eq('client_id', clientId);
  if (projectId) next = next.eq('project_id', projectId);
  return next;
};

const loadProductionAlerts = async (scope?: { clientId?: string | null; projectId?: string | null }): Promise<RawVisionAlert[]> => {
  const [alertsResult, casesResult, kvResult] = await Promise.all([
    applyScope(supabase.from('aql_alerts').select('*'), scope?.clientId, scope?.projectId).order('created_at', { ascending: false }).limit(120),
    applyScope(supabase.from('aql_cases').select('*'), scope?.clientId, scope?.projectId).order('created_at', { ascending: false }).limit(120),
    supabase.from(KV_STORE_TABLE).select('key,value').ilike('key', 'visionAlert:%').limit(120)
  ]);

  const alerts = Array.isArray(alertsResult.data)
    ? alertsResult.data.map((row) => normalizeRowToAlert(row, 'alert'))
    : [];
  const cases = Array.isArray(casesResult.data)
    ? casesResult.data.map((row) => normalizeRowToAlert(row, 'case'))
    : [];
  const kvAlerts = Array.isArray(kvResult.data)
    ? kvResult.data.map(normalizeKvAlert).filter((item): item is RawVisionAlert => Boolean(item))
    : [];

  const byId = new Map<string, RawVisionAlert>();
  [...alerts, ...cases, ...kvAlerts].forEach((item) => byId.set(item.id, item));
  return Array.from(byId.values());
};

const summaryFromCases = (cases: ClinicalCase[]): MvpDashboardSummary => {
  const newest = [...cases].sort((a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime())[0] ?? null;
  const pending = cases.filter((item) => item.validationStatus === 'pending').length;
  const critical = cases.filter((item) => item.validationStatus === 'pending' && item.severity === 'critical').length;
  const high = cases.filter((item) => item.validationStatus === 'pending' && item.severity === 'high').length;
  const warningSensor =
    newest?.oxygen !== null && newest?.oxygen !== undefined && newest.oxygen < 5.5
      ? 'O2 baixo'
      : newest?.temperature !== null && newest?.temperature !== undefined && (newest.temperature < 16 || newest.temperature > 24)
        ? 'Temperatura fora'
        : newest?.ph !== null && newest?.ph !== undefined && (newest.ph < 7.2 || newest.ph > 8.4)
          ? 'pH fora'
          : 'OK';

  return {
    status: critical > 0 ? 'Alerta' : high > 0 || warningSensor !== 'OK' ? 'Atenção' : 'OK',
    reason: critical > 0 ? `${critical} alerta crítico por validar` : high > 0 ? `${high} alerta de alta prioridade` : 'Operação sem alertas críticos pendentes',
    updatedAt: newest?.capturedAt ?? new Date().toISOString(),
    latestCapture: newest ? {
      alertId: newest.id,
      imageUrl: newest.image || null,
      capturedAt: newest.capturedAt,
      tank: newest.tank,
      phase: newest.phase,
      species: newest.species,
      detectionStatus: newest.suspectedPathology
    } : null,
    alertsActive: cases.length,
    pendingValidation: pending,
    sensors: {
      temperature: newest?.temperature ?? null,
      ph: newest?.ph ?? null,
      oxygen: newest?.oxygen ?? null,
      status: warningSensor
    },
    device: {
      online: true,
      lastCommunication: newest?.capturedAt ?? null,
      cameraOk: Boolean(newest?.image),
      sensorsOk: warningSensor === 'OK'
    }
  };
};

const severityTone = (severity: Severity) => {
  if (severity === 'critical') return 'aql-lab-critical';
  if (severity === 'high') return 'aql-lab-warning';
  if (severity === 'medium') return 'border-amber-300/55 bg-amber-300/10 text-amber-100';
  return 'aql-lab-normal';
};

const statusTone = (status?: string | null) => {
  const value = String(status ?? '').toLowerCase();
  if (value.includes('alert')) return 'aql-lab-critical';
  if (value.includes('aten')) return 'aql-lab-warning';
  return 'aql-lab-normal';
};

function MobileStat({
  icon: Icon,
  label,
  value,
  tone = 'slate'
}: {
  icon: typeof AlertTriangle;
  label: string;
  value: string | number;
  tone?: 'red' | 'amber' | 'cyan' | 'emerald' | 'slate';
}) {
  const toneClass = {
    red: 'border-red-500/45 bg-red-500/12 text-red-100',
    amber: 'border-amber-400/45 bg-amber-400/12 text-amber-100',
    cyan: 'border-cyan-400/45 bg-cyan-400/12 text-cyan-100',
    emerald: 'border-emerald-400/45 bg-emerald-400/12 text-emerald-100',
    slate: 'border-cyan-200/18 bg-cyan-950/12 text-cyan-50'
  }[tone];

  return (
    <div className={`aql-lab-stat p-3 ${toneClass}`}>
      <div className="flex items-center justify-between gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="aql-lab-mono text-right text-2xl font-black leading-none">{value}</span>
      </div>
      <p className="aql-lab-mono mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] opacity-80">{label}</p>
    </div>
  );
}

function VisionImageFrame({ src, className = 'aspect-[16/10]' }: { src: string; className?: string }) {
  return (
    <div className={`aql-lab-vision-frame ${className}`}>
      <img src={src} alt="" className="h-full w-full object-cover opacity-90 saturate-[0.86]" />
      <span className="aql-lab-vision-label">CV 0.91</span>
    </div>
  );
}

function AlertRow({ item, onOpen }: { item: ClinicalCase; onOpen?: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="aql-lab-panel grid w-full grid-cols-[56px_1fr] gap-3 p-3 text-left active:scale-[0.995]"
    >
      <div className="h-14 overflow-hidden border border-cyan-200/20 bg-[#031017]">
        {item.image ? (
          <img src={item.image} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Camera className="h-5 w-5 rotate-[-8deg] text-cyan-200/45" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-black text-white">{item.suspectedPathology}</p>
          <span className={`aql-lab-mono shrink-0 border px-2 py-0.5 text-[10px] font-black uppercase ${severityTone(item.severity)}`}>
            {item.severity}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#9fb9bd]">{item.notes}</p>
        <div className="aql-lab-mono mt-2 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-[#6f9298]">
          <span>{item.tank ?? 'Tanque sem ID'}</span>
          <span>·</span>
          <span>{formatRelative(item.capturedAt)}</span>
          {item.confidence !== null ? (
            <>
              <span>·</span>
              <span>{item.confidence}%</span>
            </>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function BottomNav({ active, onChange }: { active: MobileTab; onChange: (tab: MobileTab) => void }) {
  const items: Array<{ id: MobileTab; label: string; icon: typeof Activity }> = [
    { id: 'status', label: 'Estado', icon: Activity },
    { id: 'alerts', label: 'Alertas', icon: ShieldAlert },
    { id: 'observe', label: 'Registo', icon: FileText },
    { id: 'asset', label: 'Ativo', icon: QrCode },
    { id: 'settings', label: 'Conta', icon: Settings }
  ];

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),1rem)] pt-2">
      <div className="aql-lab-dock mx-auto grid max-w-md grid-cols-5 gap-1 p-1.5">
        {items.map((item) => {
          const Icon = item.icon;
          const selected = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              data-active={selected}
              className="aql-lab-dock-item aql-lab-mono relative flex min-h-[3.15rem] min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-bold text-[#7fa6ad] hover:bg-cyan-300/8 hover:text-cyan-100"
            >
              <Icon className={`h-4 w-4 rotate-[-7deg] transition-opacity duration-200 ${selected ? 'opacity-100' : 'opacity-62'}`} />
              <span className="max-w-full truncate">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function VisionMobileApp({ demoMode = false }: { demoMode?: boolean }) {
  const { client, setClient } = useGlobal();
  const [authToken, setAuthToken] = useState<string | null>(() => demoMode ? DEMO_AUTH_TOKEN : readStoredValue(SESSION_TOKEN_KEYS));
  const [userEmail, setUserEmail] = useState<string | null>(() => demoMode ? DEMO_USER_EMAIL : readStoredValue(SESSION_EMAIL_KEYS));
  const [tab, setTab] = useState<MobileTab>('status');
  const [loading, setLoading] = useState(true);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [cases, setCases] = useState<ClinicalCase[]>([]);
  const [summary, setSummary] = useState<MvpDashboardSummary | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<ClinicalCase | null>(null);
  const [isOnline, setIsOnline] = useState(() => typeof navigator === 'undefined' ? true : navigator.onLine);
  const [syncQueue, setSyncQueue] = useState<SyncEvent[]>(() => readSyncQueue());
  const [syncing, setSyncing] = useState(false);
  const [assetCode, setAssetCode] = useState('');
  const [observation, setObservation] = useState<ObservationForm>(() => readObservationDraft());
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<AqlOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => localStorage.getItem('aql_mobile_project_id'));
  const [zoneOptions, setZoneOptions] = useState<AqlOption[]>([]);

  const activeDemo = demoMode || authToken === DEMO_AUTH_TOKEN;
  const selectedProject = projectOptions.find((project) => project.id === selectedProjectId) ?? null;
  const selectedZone = zoneOptions.find((item) => item.id === observation.zoneId) ?? null;
  const selectedAssetCode = assetCode.trim() || selectedZone?.code || selectedZone?.name || '';

  const persistQueue = (updater: (queue: SyncEvent[]) => SyncEvent[]) => {
    setSyncQueue((current) => {
      const next = updater(current);
      writeSyncQueue(next);
      return next;
    });
  };

  const enqueueEvent = (type: SyncEventType, payload: Record<string, any>) => {
    const event: SyncEvent = {
      id: newEventId(),
      type,
      createdAt: nowIso(),
      attempts: 0,
      payload
    };
    persistQueue((queue) => [...queue, event]);
    return event;
  };

  const sendSyncEvent = async (event: SyncEvent) => {
    if (event.type === 'validate_alert') {
      const response = await fetch(`${functionsBaseUrl}/vision-alert-validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          apikey: publicAnonKey,
          'X-Session-Token': authToken ?? '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event.payload)
      });
      const data = await parseResponsePayload(response);
      if (!response.ok) throw new Error(data?.error || data?.message || 'Erro ao validar alerta');
      return;
    }

    const { error } = await supabase.from('aql_system_logs').insert({
      client_id: event.payload.client_id ?? null,
      project_id: event.payload.project_id ?? null,
      type: 'mobile_field_observation',
      level: event.payload.severity === 'critical' || event.payload.severity === 'high' ? 'warning' : 'info',
      message: event.payload.message,
      source: 'aql-vision-mobile',
      metadata: event.payload
    });
    if (error) throw error;
  };

  const flushQueue = async () => {
    if (activeDemo || syncing || !isOnline || syncQueue.length === 0) return;
    setSyncing(true);
    const remaining: SyncEvent[] = [];

    for (const event of syncQueue) {
      try {
        await sendSyncEvent(event);
      } catch (error) {
        remaining.push({
          ...event,
          attempts: event.attempts + 1,
          lastError: error instanceof Error ? error.message : String(error)
        });
      }
    }

    writeSyncQueue(remaining);
    setSyncQueue(remaining);
    setSyncing(false);
    setLastSyncedAt(nowIso());
    if (remaining.length === 0) {
      load();
    }
  };

  const loadScopeOptions = async () => {
    if (activeDemo || !authToken || !userEmail || !client?.id) return;
    setScopeLoading(true);
    try {
      const projects = await fetchProjects(client.id);
      setProjectOptions(projects);

      const savedProjectStillAllowed = selectedProjectId && projects.some((project) => project.id === selectedProjectId);
      if (projects.length === 1 && !savedProjectStillAllowed) {
        setSelectedProjectId(projects[0].id);
        localStorage.setItem('aql_mobile_project_id', projects[0].id);
      } else if (!savedProjectStillAllowed && projects.length > 0) {
        setSelectedProjectId(null);
        localStorage.removeItem('aql_mobile_project_id');
      }
    } catch (error) {
      console.error('AQL Vision mobile scope failed', error);
      toast.error('Não foi possível carregar os projetos do cliente.');
    } finally {
      setScopeLoading(false);
    }
  };

  const loadZones = async () => {
    if (activeDemo || !client?.id) return;
    try {
      const zones = await fetchZones({ clientId: client.id, projectId: selectedProjectId ?? undefined });
      setZoneOptions(zones);
      setObservation((current) => {
        if (!current.zoneId || zones.some((zone) => zone.id === current.zoneId)) return current;
        return { ...current, zoneId: '' };
      });
    } catch (error) {
      console.error('AQL Vision mobile zones failed', error);
      setZoneOptions([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      if (activeDemo) {
        const demoAlerts = getDemoVisionAlerts();
        const demoCases = normalizeAlertsToCases(demoAlerts);
        setCases(demoCases);
        setSummary(getDemoDashboardSummary(demoAlerts));
        return;
      }

      const alerts = await loadProductionAlerts({ clientId: client?.id ?? null, projectId: selectedProjectId });
      const normalized = normalizeAlertsToCases(alerts)
        .sort((a, b) => {
          const statusDelta = (a.validationStatus === 'pending' ? 0 : 1) - (b.validationStatus === 'pending' ? 0 : 1);
          if (statusDelta !== 0) return statusDelta;
          const severityDelta = severityOrder[a.severity] - severityOrder[b.severity];
          if (severityDelta !== 0) return severityDelta;
          return new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime();
        });
      setCases(normalized);
      setSummary(summaryFromCases(normalized));
    } catch (error) {
      console.error('AQL Vision mobile load failed', error);
      toast.error('Não foi possível atualizar os alertas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authToken || !userEmail) return;
    loadScopeOptions();
  }, [authToken, userEmail, client?.id]);

  useEffect(() => {
    if (!authToken || !userEmail) return;
    loadZones();
  }, [authToken, userEmail, client?.id, selectedProjectId]);

  useEffect(() => {
    if (!authToken || !userEmail) return;
    if (!activeDemo && projectOptions.length > 1 && !selectedProjectId) return;
    load();
  }, [authToken, userEmail, client?.id, selectedProjectId, projectOptions.length]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(MOBILE_OBSERVATION_DRAFT_KEY, JSON.stringify(observation));
  }, [observation]);

  useEffect(() => {
    if (!authToken || !userEmail || !isOnline || activeDemo || syncQueue.length === 0) return;
    flushQueue();
  }, [authToken, userEmail, isOnline, syncQueue.length, activeDemo]);

  const pendingCases = useMemo(() => cases.filter((item) => item.validationStatus === 'pending'), [cases]);
  const criticalCount = pendingCases.filter((item) => item.severity === 'critical').length;
  const highCount = pendingCases.filter((item) => item.severity === 'high').length;
  const latest = cases[0] ?? null;
  const latestCaptureUrl = summary?.latestCapture?.imageUrl || latest?.image || (activeDemo ? getDemoAssetUrl(DEMO_POSTER_FILE) : '');
  const assetMatches = useMemo(() => {
    const code = selectedAssetCode.toLowerCase();
    if (!code) return cases.slice(0, 4);
    return cases.filter((item) => {
      const haystack = [
        item.id,
        item.tank,
        item.site,
        item.rawAlert.alert_id,
        item.rawAlert.case_id,
        item.rawAlert.kit_id,
        item.rawAlert.metadata?.asset_code,
        item.rawAlert.metadata?.assetCode,
        item.rawAlert.metadata?.camera_id,
        item.rawAlert.metadata?.sensor_id
      ].map((value) => String(value ?? '').toLowerCase());
      return haystack.some((value) => value.includes(code));
    }).slice(0, 8);
  }, [cases, selectedAssetCode]);

  const handleLoginSuccess = (token: string, email: string) => {
    setAuthToken(token);
    setUserEmail(email);
    localStorage.setItem('visionAlertsToken', token);
    localStorage.setItem('visionAlertsEmail', email);
  };

  const handleSignOut = () => {
    SESSION_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
    SESSION_EMAIL_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(LOGIN_CLIENT_CONFIRMED_KEY);
    localStorage.removeItem('aql_mobile_project_id');
    setAuthToken(null);
    setUserEmail(null);
    setClient(null);
    setProjectOptions([]);
    setSelectedProjectId(null);
    setZoneOptions([]);
    setCases([]);
    setSummary(null);
  };

  const handleChangeProject = () => {
    if (activeDemo || projectOptions.length <= 1) return;
    localStorage.removeItem('aql_mobile_project_id');
    setSelectedProjectId(null);
    setZoneOptions([]);
    setAssetCode('');
    setObservation((current) => ({ ...current, zoneId: '' }));
    setTab('status');
  };

  const handleValidateAlert = (item: ClinicalCase, validationResult: 'confirmed_anomaly' | 'rejected_alert' | 'inconclusive') => {
    const caseId = item.rawAlert.case_id ?? item.id;
    if (!caseId) {
      toast.error('Este alerta ainda não tem caso associado.');
      return;
    }

    const nextStatus = validationResult === 'confirmed_anomaly'
      ? 'confirmed'
      : validationResult === 'rejected_alert'
        ? 'rejected'
        : 'inconclusive';
    const validationNotes =
      validationResult === 'confirmed_anomaly'
        ? 'Confirmado rapidamente em app móvel.'
        : validationResult === 'rejected_alert'
          ? 'Descartado rapidamente em app móvel.'
          : 'Marcado como inconclusivo em app móvel.';

    if (!activeDemo) {
      enqueueEvent('validate_alert', {
        case_id: caseId,
        alert_id: item.rawAlert.alert_id ?? null,
        kit_id: item.rawAlert.kit_id ?? item.rawAlert.metadata?.kit_id ?? item.rawAlert.metadata?.kitId ?? null,
        validated_by_email: userEmail,
        validated_by_name: userEmail,
        validation_result: validationResult,
        anomaly_type: validationResult === 'rejected_alert' ? 'not_applicable' : item.anomalyType ?? 'other',
        severity: item.severity,
        validation_notes: validationNotes,
        validated_at: nowIso(),
        validator: { email: userEmail, name: userEmail }
      });
    }

    setCases((current) => current.map((row) => row.id === item.id ? {
      ...row,
      validationStatus: nextStatus,
      validationResult,
      notes: `${row.notes}\n${validationNotes}`.trim()
    } : row));
    setSelectedAlert(null);
    toast.success(activeDemo ? 'Validação simulada.' : isOnline ? 'Validação enviada.' : 'Sem rede: validação guardada para envio automático.');
  };

  const handleSubmitObservation = () => {
    const note = sanitizeInput(observation.note);
    if (!note) {
      toast.error('Escreve uma observação curta antes de guardar.');
      return;
    }
    if (!observation.zoneId) {
      toast.error('Escolhe o tanque antes de guardar.');
      return;
    }

    const payload = {
      client_id: client?.id ?? latest?.clientId ?? null,
      project_id: selectedProjectId ?? latest?.projectId ?? null,
      zone_id: observation.zoneId,
      asset_code: selectedZone?.code ?? selectedZone?.name ?? null,
      category: sanitizeInput(observation.category, 80),
      severity: observation.severity,
      message: note,
      user_email: sanitizeInput(userEmail ?? '', 160),
      observed_at: nowIso(),
      app_state: isOnline ? 'online' : 'offline'
    };

    if (!activeDemo) {
      enqueueEvent('field_observation', payload);
    }
    setObservation((current) => ({ ...current, note: '' }));
    toast.success(activeDemo ? 'Observação simulada.' : isOnline ? 'Observação registada.' : 'Sem rede: observação guardada localmente.');
  };

  if (!authToken || !userEmail) {
    return (
      <UniversalSmartLogin
        apiUrl={aqlVisionApiUrl}
        publicAnonKey={publicAnonKey}
        appName="AQL Vision Mobile"
        appId="vision-alerts"
        appIcon={<AqlVisionMark className="h-8 w-8 text-white" alt="AQL Vision" />}
        appGradient="from-slate-800 to-sky-600"
        onLoginSuccess={handleLoginSuccess}
        onBack={() => window.location.assign('/')}
        requireClientSelection
      />
    );
  }

  if (!activeDemo && authToken && userEmail && (scopeLoading || (projectOptions.length > 1 && !selectedProjectId))) {
    return (
      <div
        className="aql-lab-shell px-5 pb-8"
        style={{ paddingTop: 'max(9rem, calc(env(safe-area-inset-top) + 2rem))' }}
      >
        <div className="mx-auto flex max-w-md flex-col gap-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-cyan-200/30 bg-cyan-300/8 [clip-path:polygon(18%_0,100%_0,100%_82%,82%_100%,0_100%,0_18%)]">
              <AqlVisionMark className="h-7 w-7 text-cyan-100" alt="AQL Vision" />
            </div>
            <div className="min-w-0">
              <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#6f9298]">Cliente</p>
              <h1 className="aql-lab-title truncate text-xl font-black">{client?.name ?? 'AQL Vision'}</h1>
            </div>
          </div>

          <section className="aql-lab-panel p-4">
            <h2 className="text-lg font-black">Escolher projeto</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-[#9fb9bd]">
              A tua conta tem acesso a vários projetos. Escolhe o contexto de trabalho.
            </p>

            <div className="mt-4 space-y-2">
              {scopeLoading ? (
                <AqlVisionLoader compact label="A carregar projetos..." />
              ) : projectOptions.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => {
                    setSelectedProjectId(project.id);
                    localStorage.setItem('aql_mobile_project_id', project.id);
                  }}
                  className="aql-lab-button flex min-h-14 w-full items-center justify-between px-4 text-left text-sm font-bold"
                >
                  <span className="truncate">{project.name}</span>
                  <CheckCircle2 className="h-4 w-4 text-cyan-200" />
                </button>
              ))}
            </div>
          </section>

          <button
            type="button"
            onClick={handleSignOut}
            className="aql-lab-button min-h-12 text-sm font-bold"
          >
            Mudar cliente
          </button>
        </div>
      </div>
    );
  }

  if (loading && cases.length === 0) {
    return (
      <div className="aql-lab-shell flex min-h-[100dvh] items-center justify-center px-5">
        <AqlVisionLoader label="A carregar AQL Vision Mobile..." />
      </div>
    );
  }

  return (
    <div className="aql-lab-shell">
      <header
        className="sticky top-0 z-30 border-b border-cyan-200/16 bg-[#050c12]/92 px-4 pb-3 backdrop-blur-xl"
        style={{ paddingTop: 'max(5.25rem, calc(env(safe-area-inset-top) + 1.25rem))' }}
      >
        <div className="mx-auto flex max-w-md items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-cyan-200/30 bg-cyan-300/8 [clip-path:polygon(18%_0,100%_0,100%_82%,82%_100%,0_100%,0_18%)]">
              <AqlVisionMark className="h-6 w-6 text-cyan-100" alt="AQL Vision" />
            </div>
            <div className="min-w-0">
              <h1 className="aql-lab-title truncate text-base font-black">AQL Vision</h1>
              <p className="aql-lab-mono truncate text-[11px] font-medium text-[#7fa6ad]">{userEmail}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            className="aql-lab-button flex h-10 w-10 shrink-0 items-center justify-center"
            aria-label="Atualizar"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="aql-lab-mono mx-auto mt-3 flex max-w-md items-center justify-between gap-2 text-[11px] font-bold">
          <span className={`inline-flex items-center gap-1.5 border px-2 py-1 ${
            isOnline ? 'aql-lab-normal' : 'aql-lab-warning'
          }`}>
            {isOnline ? <Signal className="h-3.5 w-3.5" /> : <SignalZero className="h-3.5 w-3.5" />}
            {isOnline ? 'Online' : 'Sem rede'}
          </span>
          <button
            type="button"
            onClick={flushQueue}
            disabled={!isOnline || syncQueue.length === 0 || syncing}
            className="aql-lab-button inline-flex min-h-7 items-center gap-1.5 px-2 py-1 disabled:opacity-55"
          >
            <UploadCloud className={`h-3.5 w-3.5 ${syncing ? 'animate-pulse' : ''}`} />
            {syncQueue.length ? `${syncQueue.length} pendente${syncQueue.length === 1 ? '' : 's'}` : lastSyncedAt ? `Sync ${formatRelative(lastSyncedAt)}` : 'Sincronizado'}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 pb-[calc(env(safe-area-inset-bottom)+9.25rem)] pt-4">
        {tab === 'status' && (
          <div className="space-y-4">
            <section className={`aql-lab-panel p-4 ${statusTone(summary?.status)}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.18em] opacity-75">Estado operacional</p>
                  <h2 className="mt-2 text-3xl font-black leading-none">{summary?.status ?? 'A aferir'}</h2>
                </div>
                <CheckCircle2 className="h-7 w-7 shrink-0" />
              </div>
              <p className="mt-3 text-sm font-medium leading-6 opacity-90">{summary?.reason ?? 'Sem resumo operacional.'}</p>
              <p className="aql-lab-mono mt-2 text-[11px] font-semibold opacity-70">Atualizado {formatRelative(summary?.updatedAt)}</p>
            </section>

            <section className="grid grid-cols-2 gap-3">
              <MobileStat icon={ShieldAlert} label="Críticos" value={criticalCount} tone={criticalCount ? 'red' : 'emerald'} />
              <MobileStat icon={AlertTriangle} label="Alta prioridade" value={highCount} tone={highCount ? 'amber' : 'emerald'} />
              <MobileStat icon={Clock} label="Pendentes" value={pendingCases.length} tone={pendingCases.length ? 'cyan' : 'emerald'} />
              <MobileStat icon={Bell} label="Total ativos" value={cases.length} tone="slate" />
            </section>

            <section className="aql-lab-panel p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-black">Última captura</h3>
                <span className="aql-lab-mono text-xs font-semibold text-[#7fa6ad]">{formatShortTime(summary?.latestCapture?.capturedAt ?? latest?.capturedAt)}</span>
              </div>
              <button type="button" onClick={() => setTab('observe')} className="block w-full overflow-hidden border border-cyan-200/20 bg-[#031017]">
                {latestCaptureUrl ? (
                  <VisionImageFrame src={latestCaptureUrl} />
                ) : (
                  <div className="flex aspect-[16/10] items-center justify-center">
                    <Camera className="h-8 w-8 rotate-[-8deg] text-cyan-200/45" />
                  </div>
                )}
              </button>
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black">A validar agora</h3>
                <button type="button" onClick={() => setTab('alerts')} className="text-xs font-bold text-cyan-300">
                  Ver todos
                </button>
              </div>
              {pendingCases.slice(0, 4).map((item) => (
                <AlertRow key={item.id} item={item} onOpen={() => setSelectedAlert(item)} />
              ))}
              {pendingCases.length === 0 && (
                <div className="aql-lab-panel aql-lab-normal p-4 text-sm font-semibold">
                  Sem alertas pendentes neste momento.
                </div>
              )}
            </section>
          </div>
        )}

        {tab === 'alerts' && (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black">Alertas</h2>
              <span className="aql-lab-mono border border-cyan-200/20 px-2 py-1 text-xs font-bold text-[#9fb9bd]">{pendingCases.length} pendentes</span>
            </div>
            {cases.map((item) => (
              <AlertRow key={item.id} item={item} onOpen={() => setSelectedAlert(item)} />
            ))}
          </section>
        )}

        {tab === 'observe' && (
          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-black">Registo de campo</h2>
            </div>
            <div className="aql-lab-panel p-3">
              <label className="aql-lab-mono text-[10px] font-black uppercase tracking-[0.16em] text-[#7fa6ad]">Tanque</label>
              <div className="mt-2 flex gap-2">
                <select
                  value={observation.zoneId}
                  onChange={(event) => {
                    const zone = zoneOptions.find((item) => item.id === event.target.value) ?? null;
                    setObservation((current) => ({ ...current, zoneId: event.target.value }));
                    setAssetCode(zone?.code || zone?.name || '');
                  }}
                  className="aql-lab-control min-h-11 flex-1 px-3 text-sm font-semibold outline-none"
                >
                  <option value="">Selecionar tanque</option>
                  {zoneOptions.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.code ? `${zone.code} · ${zone.name}` : zone.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setTab('asset')}
                  className="aql-lab-button flex h-11 w-11 shrink-0 items-center justify-center"
                  aria-label="Abrir ativo"
                >
                  <QrCode className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-2">
                <span className="aql-lab-mono text-[10px] font-black uppercase tracking-[0.16em] text-[#7fa6ad]">Tipo</span>
                <select
                  value={observation.category}
                  onChange={(event) => setObservation((current) => ({ ...current, category: event.target.value }))}
                  className="aql-lab-control h-11 w-full px-3 text-sm font-semibold outline-none"
                >
                  <option value="observacao_campo">Observação</option>
                  <option value="mortalidade">Mortalidade</option>
                  <option value="comportamento">Comportamento</option>
                  <option value="equipamento">Equipamento</option>
                  <option value="qualidade_agua">Qualidade água</option>
                </select>
              </label>
              <label className="space-y-2">
                <span className="aql-lab-mono text-[10px] font-black uppercase tracking-[0.16em] text-[#7fa6ad]">Severidade</span>
                <select
                  value={observation.severity}
                  onChange={(event) => setObservation((current) => ({ ...current, severity: event.target.value as Severity }))}
                  className="aql-lab-control h-11 w-full px-3 text-sm font-semibold outline-none"
                >
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </label>
            </div>

            <label className="block space-y-2">
              <span className="aql-lab-mono text-[10px] font-black uppercase tracking-[0.16em] text-[#7fa6ad]">Nota</span>
              <textarea
                value={observation.note}
                onChange={(event) => setObservation((current) => ({ ...current, note: event.target.value }))}
                placeholder="Escrever observação"
                rows={5}
                className="aql-lab-control w-full resize-none px-3 py-3 text-sm font-medium leading-6 outline-none placeholder:text-[#5f7d82]"
              />
              <span className="aql-lab-mono block text-[10px] font-medium leading-5 text-[#5f7d82]">
                Exemplos úteis: comportamento junto ao alimentador, mortalidade observada, equipamento fora do normal.
              </span>
            </label>

            <button
              type="button"
              onClick={handleSubmitObservation}
              className="aql-lab-primary flex h-12 w-full items-center justify-center gap-2 text-sm font-black active:scale-[0.995]"
            >
              <Send className="h-4 w-4" />
              Guardar observação
            </button>

            <div className="overflow-hidden border border-cyan-200/20 bg-[#031017]">
              {latestCaptureUrl ? (
                <VisionImageFrame src={latestCaptureUrl} className="aspect-[16/9]" />
              ) : (
                <div className="flex aspect-[16/9] items-center justify-center">
                  <Camera className="h-10 w-10 rotate-[-8deg] text-cyan-200/45" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MobileStat icon={Thermometer} label="Temp." value={summary?.sensors?.temperature ? `${summary.sensors.temperature}°` : '--'} tone="cyan" />
              <MobileStat icon={Droplets} label="O2" value={summary?.sensors?.oxygen ? `${summary.sensors.oxygen}` : '--'} tone="emerald" />
              <MobileStat icon={Waves} label="pH" value={summary?.sensors?.ph ? `${summary.sensors.ph}` : '--'} tone="slate" />
            </div>
          </section>
        )}

        {tab === 'asset' && (
          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-black">Ativo</h2>
              <p className="text-xs font-semibold text-[#7fa6ad]">Lê ou introduz um código para ver alertas e registos associados.</p>
            </div>

            <div className="aql-lab-panel p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center border border-cyan-200/45 bg-cyan-300/12 text-cyan-100 [clip-path:polygon(18%_0,100%_0,100%_82%,82%_100%,0_100%,0_18%)]">
                  <QrCode className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-100/75">Abrir contexto</p>
                  <p className="aql-lab-mono truncate text-lg font-black text-white">{selectedAssetCode || 'Sem código'}</p>
                </div>
              </div>
              <input
                value={assetCode}
                onChange={(event) => setAssetCode(event.target.value)}
                placeholder="Ler/colar QR ou código do ativo"
                className="aql-lab-control mt-4 min-h-11 w-full px-3 text-sm font-semibold outline-none placeholder:text-[#5f7d82]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <MobileStat icon={MapPin} label="Registos" value={assetMatches.length} tone="cyan" />
              <MobileStat icon={ShieldAlert} label="Pendentes" value={assetMatches.filter((item) => item.validationStatus === 'pending').length} tone="amber" />
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-black">Contexto recente</h3>
              {assetMatches.map((item) => (
                <AlertRow key={item.id} item={item} onOpen={() => setSelectedAlert(item)} />
              ))}
              {assetMatches.length === 0 && (
                <div className="aql-lab-panel p-4 text-sm font-semibold text-[#9fb9bd]">
                  Nenhum alerta encontrado para este ativo.
                </div>
              )}
            </section>
          </section>
        )}

        {tab === 'settings' && (
          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-black">Conta</h2>
              <p className="text-xs font-semibold text-[#7fa6ad]">Sessão e contexto de trabalho.</p>
            </div>

            <div className="aql-lab-panel p-4">
              <div className="space-y-3">
                <div>
                  <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#6f9298]">Utilizador</p>
                  <p className="aql-lab-mono mt-1 block max-w-full truncate text-xs font-semibold text-cyan-50">{userEmail}</p>
                </div>
                <div className="grid grid-cols-1 gap-3 border-t border-cyan-200/14 pt-3">
                  <div>
                    <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#6f9298]">Cliente</p>
                    <p className="mt-1 truncate text-sm font-black text-white">{client?.name ?? 'AQL Vision'}</p>
                  </div>
                  <div>
                    <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#6f9298]">Projeto</p>
                    <p className="mt-1 truncate text-sm font-black text-white">
                      {activeDemo ? 'Demonstração' : selectedProject?.name ?? 'Sem projeto selecionado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {syncQueue.length > 0 && (
              <div className="aql-lab-warning aql-lab-mono border p-3 text-xs font-semibold leading-5">
                {syncQueue.length} registo{syncQueue.length === 1 ? '' : 's'} por enviar.
              </div>
            )}

            {!activeDemo && projectOptions.length > 1 && (
              <button
                type="button"
                onClick={handleChangeProject}
                className="aql-lab-button flex h-12 w-full items-center justify-center gap-2 text-sm font-black"
              >
                <CheckCircle2 className="h-4 w-4" />
                Escolher outro projeto
              </button>
            )}

            <button
              type="button"
              onClick={handleSignOut}
              className="aql-lab-critical flex h-12 w-full items-center justify-center gap-2 border text-sm font-black"
            >
              <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
              Terminar sessão
            </button>
          </section>
        )}
      </main>

      {selectedAlert && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/76 p-3">
          <section className="aql-lab-panel mx-auto w-full max-w-md p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <span className={`aql-lab-mono border px-2 py-1 text-[10px] font-black uppercase ${severityTone(selectedAlert.severity)}`}>
                  {selectedAlert.severity}
                </span>
                <h3 className="mt-3 text-lg font-black">{selectedAlert.suspectedPathology}</h3>
              </div>
              <button type="button" onClick={() => setSelectedAlert(null)} className="aql-lab-button px-3 py-2 text-xs font-bold">
                Fechar
              </button>
            </div>
            <p className="text-sm leading-6 text-[#9fb9bd]">{selectedAlert.notes}</p>
            <div className="aql-lab-mono mt-4 grid grid-cols-2 gap-2 text-[11px] font-semibold text-[#7fa6ad]">
              <span>Tanque: {selectedAlert.tank ?? '--'}</span>
              <span>Confiança: {selectedAlert.confidence ?? '--'}%</span>
              <span>Fase: {selectedAlert.phase ?? '--'}</span>
              <span>Hora: {formatShortTime(selectedAlert.capturedAt)}</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => handleValidateAlert(selectedAlert, 'confirmed_anomaly')}
                className="aql-lab-critical aql-lab-mono min-h-11 whitespace-nowrap border px-1.5 text-[11px] font-black active:scale-[0.995]"
              >
                [Y] Confirmar
              </button>
              <button
                type="button"
                onClick={() => handleValidateAlert(selectedAlert, 'inconclusive')}
                className="aql-lab-warning aql-lab-mono min-h-11 whitespace-nowrap border px-1.5 text-[11px] font-black active:scale-[0.995]"
              >
                [R] Rever
              </button>
              <button
                type="button"
                onClick={() => handleValidateAlert(selectedAlert, 'rejected_alert')}
                className="aql-lab-button aql-lab-mono min-h-11 whitespace-nowrap px-1.5 text-[11px] font-black active:scale-[0.995]"
              >
                [N] Rejeitar
              </button>
            </div>
          </section>
        </div>
      )}

      <BottomNav active={tab} onChange={setTab} />
    </div>
  );
}
